import type { AstrolabeId } from "@scb/protocol";

/**
 * 규칙 파라미터. docs/01-game-rules.md의 표와 1:1로 대응한다.
 * ⚠️ 표시가 붙은 값은 공식 규칙에 없어서 추정한 것 → M1에서 확정해야 한다.
 */
export interface RulesConfig {
  /** Astrolabe별 기본 점수 */
  basePoints: Record<AstrolabeId, number>;
  /** Chaining: 추가로 연결된 공 1개당 보너스 */
  chainBonus: Record<AstrolabeId, number>;
  /**
   * Grand Slam 계산 방식.
   *  - "double": 영문 공식 규칙 — 해당 판에서 그 팀이 얻은 점수만큼 추가 (총점 2배)
   *  - "flat"  : 중국어 출처 — 판별 정액 보너스
   */
  grandSlamMode: "double" | "flat";
  /** grandSlamMode === "flat"일 때 쓰이는 정액 보너스 */
  grandSlamFlat: Record<AstrolabeId, number>;

  /** ⚠️ 팀당 공 개수 (공식 규칙 미기재) */
  ballsPerTeam: number;
  /** true면 ballsPerTeam에 팀 인원수를 곱한다 */
  scaleBallsByTeamSize: boolean;
  /** 라운드 종료 시 남은 공 1개당 점수 */
  leftoverBallPoints: number;

  /**
   * 한 판(Astrolabe)을 한 팀이 전부 채우면 그 라운드를 **즉시** 끝낸다 (만관 종료).
   *
   * 만관을 완성하면 그 판 점수가 2배가 되므로, 그 뒤로 라운드를 더 굴리면
   * 상대가 안착한 공을 때려서 만관을 깨는 역전 플레이가 나온다 (§5).
   * 즉시 끊으면 만관이 확정 보상이 되어 "판 하나를 독점한다"는 목표가 또렷해진다.
   */
  endRoundOnGrandSlam: boolean;
  /**
   * 대전에서 팀별 발사 자리를 좌/우로 고정한다 (`seating.ts`).
   * 연습 모드는 상대가 없으므로 false로 두고 테이블 전체를 쓴다.
   */
  fixedLaunchSides: boolean;

  /**
   * 안착해 있던 공이 나중 공에 맞아 빠지면 점수를 차감한다 (선택 규칙).
   *
   * false(기본, 공식 규칙): 점수는 남고 칸만 빈다 — 만관을 깨는 용도.
   * true: 빠진 공 소유 팀에서 **그 판의 기본 점수**(1/2/5)만큼 차감한다.
   *   연쇄·만관 보너스는 이미 벌어진 일이라 되돌리지 않고, 점수는 0 밑으로
   *   내려가지 않는다. 빠졌다가 같은 샷에서 다른 칸에 재안착하면(여전히 안착
   *   상태) 차감하지 않는다 — 재안착은 점수도 안 주므로 대칭이 맞는다.
   */
  knockoutPenalty: boolean;

  roundCount: number;
  roundDurationMs: number;
  overtimeDurationMs: number;
  /** ⚠️ 온라인용으로 추가한 턴 제한시간 (공식 규칙에 없음) */
  turnTimeoutMs: number;
  /** Final Sequence에서 Bank/계속 선택 제한시간 */
  finalSequenceDecideMs: number;
  /** 연속 타임아웃 이 횟수를 넘으면 AFK 판정 */
  maxConsecutiveTimeouts: number;

  /**
   * 팀 내 슈팅 순서 (공식 규칙은 1v1 기준이라 팀전 규정이 없음 — 우리가 정한 것)
   *  - "rotate"  : 팀 턴이 이어지면 다음 팀원이 쏜다
   *  - "hot-hand": 득점한 사람이 계속 쏜다
   */
  teamShotOrder: "rotate" | "hot-hand";

  /** 목표 점수 선취 방식. null이면 시간제(기본). */
  targetScore: number | null;
}

export const DEFAULT_RULES: RulesConfig = {
  basePoints: { 1: 1, 2: 2, 3: 5 },
  chainBonus: { 1: 1, 2: 2, 3: 5 },
  grandSlamMode: "double",
  grandSlamFlat: { 1: 10, 2: 20, 3: 30 },

  ballsPerTeam: 12,
  scaleBallsByTeamSize: false,
  leftoverBallPoints: 1,

  endRoundOnGrandSlam: true,
  fixedLaunchSides: true,
  knockoutPenalty: false,

  roundCount: 2,
  roundDurationMs: 6 * 60 * 1000,
  overtimeDurationMs: 3 * 60 * 1000,
  turnTimeoutMs: 20_000,
  finalSequenceDecideMs: 15_000,
  maxConsecutiveTimeouts: 3,

  teamShotOrder: "rotate",
  targetScore: null,
};

export function ballsForTeam(config: RulesConfig, teamSize: number): number {
  return config.scaleBallsByTeamSize ? config.ballsPerTeam * teamSize : config.ballsPerTeam;
}
